import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Box, Button, Chip, Divider, Grid2, IconButton, Paper, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PercentIcon from '@mui/icons-material/Percent';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SaveIcon from '@mui/icons-material/Save';
import { GqlCreditSubmissionApproval } from '../types';
import { UPDATE_PARSED_DECISION } from '../queries';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReviewExtractedTermsProps {
  creditSubmission: GqlCreditSubmissionApproval;
  parsedDecisionId: string;
  onBack: () => void;
  onSaveComplete?: () => void;
}

type FieldStatus = 'extracted' | 'edited' | 'empty';

// A field definition for the approval details panel
interface FieldDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'currency' | 'percentage';
}

const APPROVAL_FIELDS: FieldDef[] = [
  { key: 'lenderName', label: 'Lender Name', type: 'text' },
  { key: 'decision', label: 'Decision', type: 'text' },
  { key: 'applicantName', label: 'Applicant Name', type: 'text' },
  { key: 'approvalAmount', label: 'Approved Amount', type: 'currency' },
  { key: 'termLengthMonths', label: 'Term (Months)', type: 'number' },
  { key: 'monthlyPayment', label: 'Monthly Payment', type: 'currency' },
  { key: 'interestRate', label: 'Interest Rate', type: 'percentage' },
  { key: 'downPaymentToVendor', label: 'Down Payment', type: 'percentage' },
  { key: 'docFees', label: 'Doc Fees', type: 'currency' },
];

// Summary card config
interface SummaryCard {
  label: string;
  key: string;
  icon: React.ReactNode;
  format: (v: any) => string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCurrencyShort(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${Number(v).toLocaleString('en-US')}`;
}

function formatPercentage(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${v}%`;
}

function formatMonths(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${v} mo`;
}

function toNumber(v: string): number | null {
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}

function toInt(v: string): number | null {
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusIcon: React.FC<{ status: FieldStatus }> = ({ status }) => {
  if (status === 'extracted') return <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />;
  if (status === 'edited') return <EditIcon sx={{ fontSize: 18, color: 'primary.main' }} />;
  return <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />;
};

// ---------------------------------------------------------------------------
// Inline editable field row
// ---------------------------------------------------------------------------

interface EditableFieldRowProps {
  label: string;
  value: string;
  originalValue: string;
  displayValue: string;
  status: FieldStatus;
  onChange: (val: string) => void;
  onSave: () => void;
  type?: 'text' | 'number';
  isDecisionField?: boolean;
}

const EditableFieldRow: React.FC<EditableFieldRowProps> = ({
  label,
  value,
  originalValue,
  displayValue,
  status,
  onChange,
  onSave,
  type = 'text',
  isDecisionField,
}) => {
  const [editing, setEditing] = useState(false);

  const handleSave = () => {
    setEditing(false);
    onSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <StatusIcon status={status} />
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>
        {isDecisionField && !editing ? (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label={displayValue || 'Unknown'}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        ) : editing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              value={value}
              onChange={e => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              type={type}
              autoFocus
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { borderColor: 'primary.main' },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              sx={{ minWidth: 60, textTransform: 'uppercase', fontWeight: 600 }}
            >
              Save
            </Button>
          </Box>
        ) : (
          <Box
            onClick={() => setEditing(true)}
            sx={{
              cursor: 'pointer',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="body2" color={status === 'empty' ? 'text.disabled' : 'text.primary'}>
              {status === 'empty' ? 'Not extracted — click to add' : displayValue}
            </Typography>
            {status === 'edited' && (
              <Typography variant="caption" color="primary.main" fontWeight={600}>
                Edited
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ReviewExtractedTerms: React.FC<ReviewExtractedTermsProps> = ({
  creditSubmission,
  parsedDecisionId,
  onBack,
  onSaveComplete,
}) => {
  const parsed = creditSubmission.creditDecisionParsedData?.parsedData;
  const lenderName = creditSubmission.lenderName ?? 'Unknown Lender';

  const [updateParsedDecision, { loading: saving }] = useMutation(UPDATE_PARSED_DECISION, {
    refetchQueries: ['GetApprovalsByVendorOpportunityId'],
  });

  // Build initial values from parsed data
  const initialValues = useMemo(() => {
    if (!parsed) return {} as Record<string, string>;
    const docFeeTotal = (parsed.lenderFees ?? [])
      .filter((f: any) => f?.feeName?.toLowerCase()?.includes('doc'))
      .reduce((sum: number, f: any) => sum + (f?.amount ?? 0), 0);
    return {
      lenderName: lenderName,
      decision: creditSubmission.decision ?? parsed.decisionType ?? '',
      applicantName: parsed.applicantName ?? '',
      approvalAmount: parsed.approvalAmount != null ? String(parsed.approvalAmount) : '',
      termLengthMonths: parsed.termLengthMonths != null ? String(parsed.termLengthMonths) : '',
      monthlyPayment: parsed.monthlyPayment != null ? String(parsed.monthlyPayment) : '',
      interestRate: parsed.interestRate != null ? String(parsed.interestRate) : '',
      downPaymentToVendor: parsed.downPaymentToVendor != null ? String(parsed.downPaymentToVendor) : '',
      docFees: docFeeTotal > 0 ? String(docFeeTotal) : '',
    };
  }, [parsed, lenderName, creditSubmission.decision]);

  const [form, setForm] = useState<Record<string, string>>(initialValues);
  const [conditions, setConditions] = useState<string[]>([]);

  // Sync from data
  useEffect(() => {
    setForm(initialValues);
    setConditions((parsed?.conditions ?? []).filter((c: any): c is string => c != null && c.trim() !== ''));
  }, [initialValues, parsed]);

  const originalValues = initialValues;

  const getFieldStatus = useCallback(
    (key: string): FieldStatus => {
      const current = form[key] ?? '';
      const original = originalValues[key] ?? '';
      if (!current && !original) return 'empty';
      if (current !== original) return 'edited';
      return current ? 'extracted' : 'empty';
    },
    [form, originalValues]
  );

  const getDisplayValue = useCallback(
    (key: string, fDef: FieldDef): string => {
      const val = form[key] ?? '';
      if (!val) return '';
      const num = parseFloat(val);
      switch (fDef.type) {
        case 'currency':
          return isNaN(num) ? val : formatCurrency(num);
        case 'percentage':
          return isNaN(num) ? val : formatPercentage(num);
        default:
          return val;
      }
    },
    [form]
  );

  const handleFieldChange = (key: string) => (val: string) => {
    setForm(prev => ({ ...prev, [key]: val }));
  };

  // Conditions management
  const handleRemoveCondition = (index: number) => {
    setConditions(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddCondition = () => {
    setConditions(prev => [...prev, '']);
  };

  const handleConditionChange = (index: number, value: string) => {
    setConditions(prev => prev.map((c, i) => (i === index ? value : c)));
  };

  // Save handler
  const handleSaveAll = async () => {
    const lenderFees = parsed?.lenderFees ?? [];

    const parsedData: Record<string, any> = {
      decisionType: form.decision || null,
      approvalAmount: toNumber(form.approvalAmount ?? ''),
      termLengthMonths: toInt(form.termLengthMonths ?? ''),
      interestRate: toNumber(form.interestRate ?? ''),
      monthlyPayment: toNumber(form.monthlyPayment ?? ''),
      downPaymentToVendor: toNumber(form.downPaymentToVendor ?? ''),
      applicantName: form.applicantName || null,
      businessName: parsed?.businessName || null,
      conditions: conditions.filter(c => c.trim() !== ''),
      stipulations: parsed?.stipulations ?? [],
      declineReasons: parsed?.declineReasons ?? [],
      lenderFees,
      firstPaymentDueDate: parsed?.firstPaymentDueDate || null,
      advancePaymentsCount: parsed?.advancePaymentsCount ?? null,
      decisionDate: parsed?.decisionDate || null,
      expirationDate: parsed?.expirationDate || null,
    };

    await updateParsedDecision({
      variables: { input: { parsedDecisionId, parsedData } },
    });

    onSaveComplete?.();
    onBack();
  };

  // Summary cards data
  const summaryCards: SummaryCard[] = [
    {
      label: 'Approved Amount',
      key: 'approvalAmount',
      icon: <AttachMoneyIcon sx={{ fontSize: 18, color: '#2196f3' }} />,
      format: (v: any) => formatCurrencyShort(toNumber(v ?? '')),
    },
    {
      label: 'Term',
      key: 'termLengthMonths',
      icon: <CalendarTodayIcon sx={{ fontSize: 18, color: '#4caf50' }} />,
      format: (v: any) => formatMonths(toInt(v ?? '')),
    },
    {
      label: 'Monthly Payment',
      key: 'monthlyPayment',
      icon: <ReceiptLongIcon sx={{ fontSize: 18, color: '#2196f3' }} />,
      format: (v: any) => formatCurrency(toNumber(v ?? '')),
    },
    {
      label: 'Rate',
      key: 'interestRate',
      icon: <PercentIcon sx={{ fontSize: 18, color: '#ff9800' }} />,
      format: (v: any) => formatPercentage(toNumber(v ?? '')),
    },
    {
      label: 'Down Payment',
      key: 'downPaymentToVendor',
      icon: <PercentIcon sx={{ fontSize: 18, color: '#9c27b0' }} />,
      format: (v: any) => formatPercentage(toNumber(v ?? '')),
    },
    {
      label: 'Doc Fees',
      key: 'docFees',
      icon: <AttachMoneyIcon sx={{ fontSize: 18, color: '#2196f3' }} />,
      format: (v: any) => formatCurrencyShort(toNumber(v ?? '')),
    },
    {
      label: 'Conditions',
      key: '_conditions',
      icon: <ListAltIcon sx={{ fontSize: 18, color: '#2196f3' }} />,
      format: () => String(conditions.length),
    },
  ];

  if (!parsed) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No parsed data available.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Back button + header */}
      <Box
        onClick={onBack}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: 1, color: 'primary.main' }}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
          Back
        </Typography>
      </Box>

      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        Review Extracted Terms
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Verify each field, click to edit, then confirm to save to submission.
      </Typography>

      {/* Business name & loan type */}
      <Typography variant="h6" fontWeight={700}>
        {parsed.businessName || lenderName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {parsed.decisionType || 'Standard Term Loan'}
      </Typography>

      {/* Summary cards row */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        {summaryCards.map(card => {
          const val = card.key === '_conditions' ? String(conditions.length) : (form[card.key] ?? '');
          const status = card.key === '_conditions' ? 'extracted' : getFieldStatus(card.key);
          return (
            <Paper
              key={card.key}
              variant="outlined"
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                minWidth: 110,
                flex: '1 1 0',
                borderColor: status === 'edited' ? 'primary.main' : 'divider',
                borderWidth: status === 'edited' ? 2 : 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {card.label}
                </Typography>
                {card.icon}
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {card.format(val)}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Two-column layout */}
      <Grid2 container spacing={3}>
        {/* Left: Approval Details */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Approval Details
              </Typography>
            </Box>
            {APPROVAL_FIELDS.map(fDef => {
              const status = getFieldStatus(fDef.key);
              return (
                <EditableFieldRow
                  key={fDef.key}
                  label={fDef.label}
                  value={form[fDef.key] ?? ''}
                  originalValue={originalValues[fDef.key] ?? ''}
                  displayValue={getDisplayValue(fDef.key, fDef)}
                  status={status}
                  onChange={handleFieldChange(fDef.key)}
                  onSave={handleSaveAll}
                  type={
                    fDef.type === 'currency' || fDef.type === 'number' || fDef.type === 'percentage' ? 'number' : 'text'
                  }
                  isDecisionField={fDef.key === 'decision'}
                />
              );
            })}
          </Paper>
        </Grid2>

        {/* Right: Conditions & Stipulations */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Conditions &amp; Stipulations
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {conditions.map((condition, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24, pt: 1, fontWeight: 600 }}>
                    {index + 1}.
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    value={condition}
                    onChange={e => handleConditionChange(index, e.target.value)}
                    placeholder="Enter Condition"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveCondition(index)}
                    sx={{ mt: 0.5, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              {/* Empty slot for next entry */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24, fontWeight: 600 }}>
                  {conditions.length + 1}.
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter Condition"
                  variant="outlined"
                  disabled
                  sx={{ opacity: 0.5 }}
                />
              </Box>

              <Divider sx={{ my: 1 }} />
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={handleAddCondition}
                sx={{ textTransform: 'uppercase', fontWeight: 600 }}
              >
                Add Condition
              </Button>
            </Box>
          </Paper>

          {/* Save button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
              disabled={saving}
              sx={{ fontWeight: 600 }}
            >
              {saving ? 'Saving…' : 'Save All Changes'}
            </Button>
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};
